import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  AlertTriangle, MessageSquare, Sparkles, TrendingUp, Copy, Download,
  MessageCircle, Send, Mic, MicOff, Paperclip, Trash2, Lock, X, Image,
  Shield, CheckCheck, Phone, Edit3, ChevronDown
} from 'lucide-react';
import jsPDF from 'jspdf';
import { addPdfHeader, addPdfFooterToAllPages, ensureImagesLoaded } from '../utils/pdfBranding';

import API from '../utils/api';
import useActivityFeed from '../hooks/useActivityFeed';
var BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

var GRADE_STYLES = {
  Green: { bg: 'bg-[#10B981]', text: 'text-[#10B981]', border: 'border-[#10B981]/30', label: 'Positive', desc: 'Respectful, cooperative, child-focused communication' },
  Blue: { bg: 'bg-[#3B82F6]', text: 'text-[#3B82F6]', border: 'border-[#3B82F6]/30', label: 'Neutral', desc: 'Factual, logistical, non-inflammatory communication' },
  Yellow: { bg: 'bg-[#EAB308]', text: 'text-[#EAB308]', border: 'border-[#EAB308]/30', label: 'Concerning', desc: 'Contains sarcasm, blame, or emotionally loaded language that may inflame conflict' },
  Orange: { bg: 'bg-[#F97316]', text: 'text-[#F97316]', border: 'border-[#F97316]/30', label: 'High Conflict', desc: 'Hostile, aggressive, or inflammatory — strongly consider the calmer alternative' },
  Red: { bg: 'bg-[#EF4444]', text: 'text-[#EF4444]', border: 'border-[#EF4444]/30', label: 'Blocked', desc: 'This message violates communication safety rules and cannot be sent' },
};

/* ==================== CONSENT GATE ==================== */
function ConsentGate({ onAccept }) {
  var [checked, setChecked] = useState(false);
  return (
    <div className="card-surface p-6 max-w-2xl mx-auto space-y-4" data-testid="messaging-consent">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-[#2D2560]" />
        <h3 className="text-lg font-semibold">Messaging Terms</h3>
      </div>
      <div className="bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-xl p-4 text-sm text-[color:var(--text-secondary)] leading-relaxed space-y-3">
        <p>By using co-parent messaging, you agree to communicate respectfully and not send threatening, abusive, violent, harassing, or self-harm related content.</p>
        <p>Messages may be reviewed by automated safety systems before sending, and prohibited content may be blocked and routed for admin review. Repeated abusive or high-conflict use may result in temporary suspension of messaging access.</p>
        <p>Although messages are encrypted, do not use chat to share highly sensitive financial, medical, court-protected, or otherwise confidential information. You remain responsible for the messages you send.</p>
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-checkbox-label">
        <input type="checkbox" checked={checked} onChange={function(e) { setChecked(e.target.checked); }}
          className="w-4 h-4 mt-1 rounded accent-[#2D2560]" data-testid="consent-checkbox" />
        <span className="text-sm text-[color:var(--text-muted)]">I understand and agree to these messaging terms</span>
      </label>
      <button onClick={onAccept} disabled={!checked}
        className="w-full py-3 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-xl font-medium disabled:opacity-30"
        data-testid="consent-accept-btn">
        Accept &amp; Continue
      </button>
    </div>
  );
}

/* ==================== PRE-SEND REVIEW MODAL ==================== */
function ReviewModal({ review, originalText, onSendOriginal, onSendRewrite, onEdit, onCancel }) {
  var grade = GRADE_STYLES[review.category] || GRADE_STYLES.Blue;
  var isBlocked = review.blocked;
  var showCrisis = review.show_crisis;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="review-modal">
      <div className="bg-white border border-[color:var(--border-subtle)] rounded-2xl p-5 max-w-lg w-full mx-4 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Communication Review</h3>
          {!isBlocked && <button onClick={onCancel} className="text-[color:var(--text-soft)] hover:text-[color:var(--text-primary)]"><X className="w-4 h-4" /></button>}
        </div>

        {/* Grade Badge */}
        <div className={'p-4 rounded-xl border ' + grade.border + ' bg-[#F4F2FB]'}>
          <div className="flex items-center gap-3 mb-2">
            <span className={'px-3 py-1 rounded-full text-xs font-bold text-white ' + grade.bg}>{review.category}</span>
            <span className={'text-sm font-semibold ' + grade.text}>{grade.label}</span>
          </div>
          <p className="text-xs text-[color:var(--text-muted)]">{review.explanation || grade.desc}</p>
        </div>

        {/* Original Message Preview */}
        <div>
          <p className="text-xs text-[color:var(--text-soft)] uppercase tracking-wider mb-1">Your Message</p>
          <div className="bg-[#F4F2FB] rounded-lg p-3 text-sm text-[color:var(--text-secondary)]">{originalText}</div>
        </div>

        {/* AI Rewrite (for Yellow/Orange/Blue) */}
        {!isBlocked && review.rewrite && (
          <div>
            <p className="text-xs text-[#10B981] uppercase tracking-wider mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Calmer Alternative
            </p>
            <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-3 text-sm text-[color:var(--text-secondary)]">{review.rewrite}</div>
          </div>
        )}

        {/* Crisis Support (for self-harm detected) */}
        {showCrisis && (
          <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4" data-testid="crisis-notice">
            <div className="flex items-start gap-2">
              <Phone className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#EF4444]">Crisis Support Available</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">If you are experiencing suicidal thoughts, considering self-harm, or thinking about harming someone else, call or text <strong className="text-[#DC2626]">988</strong> for immediate crisis support.</p>
                <p className="text-xs text-[color:var(--text-soft)] mt-1">If you are in immediate danger, call <strong className="text-[#DC2626]">911</strong>.</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {isBlocked ? (
          <div className="space-y-3">
            <div className="bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg p-3 text-center">
              <p className="text-xs text-[#EF4444] font-medium">This message cannot be sent. It has been logged for review.</p>
            </div>
            <button onClick={onCancel}
              className="w-full py-2.5 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] rounded-xl text-sm hover:bg-[#F8F7FC]"
              data-testid="review-dismiss-btn">
              Dismiss
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {review.rewrite && (
              <button onClick={onSendRewrite}
                className="w-full py-2.5 bg-gradient-to-r from-[#10B981] to-[#5A4E9E] text-white rounded-xl text-sm font-medium"
                data-testid="review-send-rewrite-btn">
                Send Calmer Version
              </button>
            )}
            <button onClick={onSendOriginal}
              className={'w-full py-2.5 rounded-xl text-sm font-medium border ' +
                (review.category === 'Orange' ? 'bg-[#F97316]/10 border-[#F97316]/30 text-[#F97316]' : 'bg-[#F4F2FB] border-[color:var(--border-subtle)] text-[color:var(--text-secondary)]')}
              data-testid="review-send-original-btn">
              Send Original
            </button>
            <div className="flex gap-2">
              <button onClick={onEdit} className="flex-1 py-2 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] rounded-xl text-xs hover:bg-[#F8F7FC]" data-testid="review-edit-btn">
                <Edit3 className="w-3 h-3 inline mr-1" />Edit Message
              </button>
              <button onClick={onCancel} className="flex-1 py-2 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] rounded-xl text-xs hover:bg-[#F8F7FC]" data-testid="review-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== MESSAGE BUBBLE ==================== */
function MessageBubble({ m, onDelete, showReadReceipt }) {
  var isMine = m.is_mine;
  var isAttachment = m.msg_type === 'attachment';
  var isImage = isAttachment && m.attachment_url && /\.(png|jpg|jpeg|gif|webp)/i.test(m.attachment_url);

  // Traditional SMS-style: your own messages = solid deep purple + white text,
  // partner's messages = solid light lavender + dark purple text.
  var bubbleBg = isMine ? '#2D2560' : '#EDE9FE';
  var bubbleText = isMine ? '#FFFFFF' : '#2D2560';
  var timeColor = isMine ? '#C9C5D9' : '#6B6690';

  return (
    <div className={'flex ' + (isMine ? 'justify-end' : 'justify-start')} data-testid={'msg-' + m.id}>
      <div
        className={'max-w-[75%] group relative px-3.5 py-2.5 shadow-sm ' + (isMine
          ? 'rounded-2xl rounded-br-md'
          : 'rounded-2xl rounded-bl-md')}
        style={{ background: bubbleBg, color: bubbleText }}>
        {isImage ? (
          <img src={BACKEND_URL + m.attachment_url} alt={m.attachment_name || 'attachment'}
            className="max-w-full max-h-48 rounded-lg" loading="lazy" />
        ) : isAttachment ? (
          <a href={BACKEND_URL + m.attachment_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 hover:underline text-sm" style={{ color: bubbleText }}>
            <Paperclip className="w-3.5 h-3.5" />{m.attachment_name || 'File'}
          </a>
        ) : (
          <p className="text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: bubbleText }}>{m.content}</p>
        )}
        <div className="flex items-center justify-between mt-1.5 gap-3">
          <span className="text-[11px]" style={{ color: timeColor }}>{m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
          <div className="flex items-center gap-1.5">
            {isMine && showReadReceipt && m.read && (
              <CheckCheck className="w-3 h-3" style={{ color: timeColor }} title="Read" />
            )}
            {isMine && (
              <button onClick={function () { onDelete(m.id); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-300"
                style={{ color: timeColor }}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================== CO-PARENT CHAT ==================== */
function CoParentChat({ ws }) {
  var [data, setData] = useState({ linked: false, messages: [], partner_name: null, read_receipts_enabled: true, partner_shares_receipts: true });
  var [input, setInput] = useState('');
  var [sending, setSending] = useState(false);
  var [reviewing, setReviewing] = useState(false);
  var [reviewResult, setReviewResult] = useState(null);
  var [pendingText, setPendingText] = useState('');
  var [recording, setRecording] = useState(false);
  var [recordingTime, setRecordingTime] = useState(0);
  var [blocked, setBlocked] = useState(null);
  var [suspensionMsg, setSuspensionMsg] = useState(null);
  var [exportDays, setExportDays] = useState(null);
  var [exporting, setExporting] = useState(false);
  var pollRef = useRef(null);
  var mediaRecorderRef = useRef(null);
  var chunksRef = useRef([]);
  var timerRef = useRef(null);
  var fileInputRef = useRef(null);

  // Sticky-bottom auto-scroll + "X new" unread badge for the chat feed.
  // Same hook used by the Video Call activity feed.
  var chatFeed = useActivityFeed({
    totalCount: (data.messages || []).length,
    watchKey: (data.messages && data.messages.length > 0) ? data.messages[data.messages.length - 1].id : '',
  });

  var loadMessages = useCallback(function () {
    axios.get(API + '/coparent-messages', { withCredentials: true })
      .then(function (r) { setData(r.data); })
      .catch(function () {});
  }, []);

  useEffect(function () {
    loadMessages();
    // Mark messages as read
    axios.post(API + '/coparent-messages/mark-read', {}, { withCredentials: true }).catch(function(){});
    pollRef.current = setInterval(function() {
      loadMessages();
      axios.post(API + '/coparent-messages/mark-read', {}, { withCredentials: true }).catch(function(){});
    }, 30000);
    return function () { clearInterval(pollRef.current); };
  }, [loadMessages]);

  useEffect(function () {
    if (ws && ws.onMessage) {
      ws.onMessage(function (msg) {
        setData(function (prev) {
          return Object.assign({}, prev, { messages: prev.messages.concat([msg]) });
        });
        // Auto mark read
        axios.post(API + '/coparent-messages/mark-read', {}, { withCredentials: true }).catch(function(){});
      });
    }
  }, [ws]);

  /* === MESSAGE PDF EXPORT === */
  async function exportMessagesPDF(days) {
    setExporting(true);
    setExportDays(null);
    try {
      await ensureImagesLoaded();
      var res = await axios.get(API + '/coparent-messages/export?days=' + days, { withCredentials: true });
      var msgs = res.data.messages || [];
      if (msgs.length === 0) { alert('No messages found for the last ' + days + ' days.'); setExporting(false); return; }
      var pdf = new jsPDF();
      pdf.setFont('times', 'normal');
      var pw = pdf.internal.pageSize.getWidth();
      var ph = pdf.internal.pageSize.getHeight();
      var y = addPdfHeader(pdf);
      pdf.setFontSize(12); pdf.setTextColor(0, 0, 0);
      pdf.text('Co-Parent Message Log', 15, y); y += 7;
      pdf.setFontSize(8); pdf.setTextColor(100, 100, 100);
      pdf.text('Between: ' + (res.data.user_name || 'You') + ' and ' + (res.data.partner_name || 'Co-Parent'), 15, y); y += 5;
      pdf.text('Period: Last ' + days + ' days (' + msgs.length + ' messages) | Generated: ' + new Date().toLocaleString(), 15, y); y += 8;
      pdf.setDrawColor(200, 200, 200); pdf.line(15, y, pw - 15, y); y += 5;
      msgs.forEach(function (m) {
        if (y > ph - 55) { pdf.addPage(); y = 20; }
        var sender = m.is_mine ? (res.data.user_name || 'You') : (res.data.partner_name || 'Co-Parent');
        var ts = m.created_at ? new Date(m.created_at).toLocaleString() : '';
        pdf.setFont('times', 'italic'); pdf.setFontSize(8); pdf.setTextColor(100, 100, 100);
        pdf.text(sender + '  |  ' + ts, 15, y); y += 5;
        pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(30, 30, 30);
        var content = m.content || m.transcript || '[attachment]';
        var lines = pdf.splitTextToSize(content, pw - 30);
        pdf.text(lines, 15, y); y += lines.length * 7 + 4;
      });
      addPdfFooterToAllPages(pdf);
      pdf.save('parentum-messages-' + days + 'days-' + Date.now() + '.pdf');
    } catch (e) { alert('Export failed'); }
    setExporting(false);
  }

  /* === PRE-SEND REVIEW FLOW === */
  function handleSendClick(e) {
    e.preventDefault();
    if (!input.trim() || sending || reviewing) return;
    var text = input.trim();
    setPendingText(text);
    setReviewing(true);
    setBlocked(null);
    setSuspensionMsg(null);
    axios.post(API + '/coparent-messages/review', { message: text }, { withCredentials: true })
      .then(function (r) {
        if (r.data.suspended) {
          setSuspensionMsg(r.data.reason);
          setReviewing(false);
          return;
        }
        // Green messages: send immediately without modal
        if (r.data.category === 'Green' && !r.data.blocked) {
          actualSend(text, 'original');
          setReviewing(false);
          return;
        }
        setReviewResult(r.data);
      })
      .catch(function (err) {
        // Fallback: if review fails, send directly
        actualSend(text, 'original');
        setReviewing(false);
      });
  }

  function actualSend(text, choice) {
    setSending(true);
    setInput('');
    setReviewResult(null);
    setReviewing(false);
    axios.post(API + '/coparent-messages', { message: text, send_choice: choice }, { withCredentials: true })
      .then(function (r) {
        if (r.data.blocked) setBlocked(r.data);
        else loadMessages();
      })
      .catch(function () {})
      .finally(function () { setSending(false); });
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this message?')) return;
    axios.delete(API + '/coparent-messages/' + id, { withCredentials: true })
      .then(function () { loadMessages(); });
  }

  function startRecording() {
    setBlocked(null);
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(function (stream) {
        var mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mr;
        chunksRef.current = [];
        mr.ondataavailable = function (e) { if (e.data.size > 0) chunksRef.current.push(e.data); };
        mr.onstop = function () {
          stream.getTracks().forEach(function (t) { t.stop(); });
          sendVoice(new Blob(chunksRef.current, { type: 'audio/webm' }));
        };
        mr.start();
        setRecording(true);
        setRecordingTime(0);
        timerRef.current = setInterval(function () { setRecordingTime(function (t) { return t + 1; }); }, 1000);
      })
      .catch(function () { setBlocked({ reason: 'Microphone access denied.' }); });
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  }

  function sendVoice(blob) {
    setSending(true);
    var fd = new FormData();
    fd.append('file', blob, 'voice.webm');
    axios.post(API + '/coparent-messages/voice', fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } })
      .then(function (r) { if (r.data.blocked) setBlocked(r.data); else loadMessages(); })
      .catch(function () { setBlocked({ reason: 'Failed to send voice message.' }); })
      .finally(function () { setSending(false); });
  }

  function handleFileSelect(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setSending(true);
    var fd = new FormData();
    fd.append('file', file);
    axios.post(API + '/coparent-messages/attachment', fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } })
      .then(function (r) { if (r.data.blocked) setBlocked(r.data); else loadMessages(); })
      .catch(function (err) { setBlocked({ reason: (err.response && err.response.data && err.response.data.detail) || 'Failed to send.' }); })
      .finally(function () { setSending(false); });
  }

  function formatTime(s) { var m = Math.floor(s / 60); var sec = s % 60; return m + ':' + (sec < 10 ? '0' : '') + sec; }

  if (!data.linked) {
    return (
      <div className="card-surface p-8 text-center" data-testid="no-coparent-linked">
        <MessageCircle className="w-8 h-8 text-[color:var(--text-soft)] mx-auto mb-3" />
        <p className="text-[color:var(--text-soft)] text-sm">No co-parent linked yet.</p>
        <p className="text-[color:var(--text-soft)] text-xs mt-1">Use the Co-Parent Link page to connect with your co-parent.</p>
      </div>
    );
  }

  var showPartnerReceipts = data.partner_shares_receipts;

  return (
    <div>
      <div className="card-surface overflow-hidden" data-testid="coparent-chat">
        {/* Chat Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--border-subtle)]">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#5A4E9E] to-[#10B981] flex items-center justify-center text-xs font-bold">
            {(data.partner_name || '?')[0].toUpperCase()}
          </div>
          <span className="text-sm text-[color:var(--text-secondary)]">{data.partner_name || 'Co-Parent'}</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981]">linked</span>
          {ws && ws.partnerOnline && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] flex items-center gap-1" data-testid="partner-online">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>online
            </span>
          )}
          {ws && ws.partnerTyping && (
            <span className="text-xs text-[#5A4E9E] italic animate-pulse" data-testid="partner-typing">typing...</span>
          )}
          <Lock className="w-3 h-3 text-[#10B981]/40 ml-auto" />
          <span className="text-xs text-[color:var(--text-soft)]">encrypted</span>
          <div className="relative ml-2">
            <button onClick={function () { setExportDays(exportDays ? null : 'open'); }} disabled={exporting}
              className="p-1.5 rounded-lg hover:bg-[#F4F2FB] text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)] transition-colors"
              data-testid="export-messages-btn" title="Export messages to PDF">
              {exporting ? <div className="w-4 h-4 border-2 border-current/30 border-t-transparent rounded-full animate-spin"></div> : <Download className="w-4 h-4" />}
            </button>
            {exportDays === 'open' && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-[color:var(--border-subtle)] rounded-xl shadow-2xl z-20 py-1 min-w-[140px]" data-testid="export-days-dropdown">
                {[30, 60, 120, 180].map(function (d) {
                  return (
                    <button key={d} onClick={function () { exportMessagesPDF(d); }}
                      className="w-full text-left px-3 py-2 text-xs text-[color:var(--text-muted)] hover:bg-[#F4F2FB] hover:text-[color:var(--text-primary)] transition-colors"
                      data-testid={'export-' + d + '-days'}>
                      Last {d} days
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div
          ref={chatFeed.feedRef}
          onScroll={chatFeed.onScroll}
          className="relative max-h-[420px] overflow-y-auto px-4 py-3 space-y-2.5"
          data-testid="messages-container"
        >
          {data.messages.length === 0 && (
            <p className="text-center text-[color:var(--text-soft)] text-xs py-6">No messages yet. Start a conversation.</p>
          )}
          {data.messages.map(function (m) {
            return <MessageBubble key={m.id} m={m} onDelete={handleDelete} showReadReceipt={showPartnerReceipts} />;
          })}
          {chatFeed.showJumpToLatest && (
            <button
              type="button"
              onClick={chatFeed.jumpToLatest}
              className="sticky bottom-2 ml-auto block mr-2 px-3 py-1.5 rounded-full bg-[#2D2560] text-white text-xs font-semibold shadow-md hover:bg-[#5A4E9E] transition-colors"
              data-testid="chat-jump-latest"
              aria-label={chatFeed.unreadCount > 0 ? chatFeed.unreadCount + ' new — jump to latest' : 'Jump to latest message'}
            >
              ↓ {chatFeed.unreadCount > 0 ? (chatFeed.unreadCount > 99 ? '99+ new' : chatFeed.unreadCount + ' new') : 'Jump to latest'}
            </button>
          )}
        </div>

        {/* Suspension Warning */}
        {suspensionMsg && (
          <div className="mx-3 mb-2 p-3 bg-[#F97316]/10 border border-[#F97316]/20 rounded-xl flex items-start gap-2" data-testid="suspension-warning">
            <AlertTriangle className="w-4 h-4 text-[#F97316] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-[#F97316] font-medium">Messaging Temporarily Disabled</p>
              <p className="text-xs text-[color:var(--text-muted)] mt-0.5">{suspensionMsg}</p>
            </div>
            <button onClick={function () { setSuspensionMsg(null); }} className="text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)]"><X className="w-3 h-3" /></button>
          </div>
        )}

        {/* Blocked Notice */}
        {blocked && (
          <div className="mx-3 mb-2 p-3 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl flex items-start gap-2" data-testid="blocked-warning">
            <AlertTriangle className="w-4 h-4 text-[#EF4444] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-[#EF4444] font-medium">Content Blocked</p>
              <p className="text-xs text-[color:var(--text-muted)] mt-0.5">{blocked.reason}</p>
            </div>
            <button onClick={function () { setBlocked(null); }} className="text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)]"><X className="w-3 h-3" /></button>
          </div>
        )}

        {/* Inline Review Panel moved BELOW the messaging portal — see after </card-surface> */}

        {/* Recording */}
        {recording && (
          <div className="mx-3 mb-2 p-3 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-[#EF4444] animate-pulse"></div>
            <span className="text-sm text-[color:var(--text-secondary)] font-mono">{formatTime(recordingTime)}</span>
            <span className="text-xs text-[color:var(--text-soft)]">Recording...</span>
            <button onClick={stopRecording} className="ml-auto p-1.5 rounded-lg bg-[#EF4444]/20 hover:bg-[#EF4444]/30">
              <MicOff className="w-4 h-4 text-[#EF4444]" />
            </button>
          </div>
        )}

        {/* Input */}
        <div className="px-3 py-2 border-t border-[color:var(--border-subtle)] space-y-0">
          <div className="flex items-center gap-2">
            <button onClick={function () { if (fileInputRef.current) fileInputRef.current.click(); }}
              disabled={sending || recording || reviewing}
              className="p-2 rounded-xl text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)] hover:bg-[#F4F2FB] disabled:opacity-20"
              data-testid="attach-file-btn">
              <Paperclip className="w-4 h-4" />
            </button>
            <input ref={fileInputRef} type="file" className="hidden"
              accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.doc,.docx,.txt"
              onChange={handleFileSelect} data-testid="file-input" />

            <form onSubmit={handleSendClick} className="flex-1 flex gap-2">
              <input type="text" value={input} onChange={function (e) { setInput(e.target.value); if (ws && ws.sendTyping) ws.sendTyping(); }}
                disabled={recording || reviewing}
                className="flex-1 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-primary)] text-[13px] rounded-xl px-3 py-2 focus:outline-none focus:border-[#5A4E9E]/40 disabled:opacity-30"
                placeholder="Message your co-parent..."
                data-testid="coparent-message-input" />
              <button type="submit" disabled={sending || !input.trim() || recording || reviewing}
                className="p-2 rounded-xl bg-[#5A4E9E] text-white hover:opacity-90 disabled:opacity-30"
                data-testid="coparent-send-btn">
                <Send className="w-4 h-4" />
              </button>
            </form>

            {!recording ? (
              <button onClick={startRecording} disabled={sending || reviewing}
                className="p-2 rounded-xl text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)] hover:bg-[#F4F2FB] disabled:opacity-20"
                data-testid="voice-btn">
                <Mic className="w-4 h-4" />
              </button>
            ) : null}
          </div>
          <p className="text-xs text-[color:var(--text-soft)] px-1 pt-1">Messages are AI-reviewed before sending. Abusive content is automatically blocked.</p>
        </div>
      </div>

      {/* ===== AI REVIEW PANEL — rendered BELOW the messaging portal ===== */}
      {reviewing && !reviewResult && (
        <div className="mt-3 p-3 bg-[#5A4E9E]/10 border border-[#5A4E9E]/20 rounded-xl flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-[#5A4E9E] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-xs text-[#5A4E9E]">AI is reviewing your message...</span>
        </div>
      )}

      {reviewResult && (function () {
        var grade = GRADE_STYLES[reviewResult.category] || GRADE_STYLES.Blue;
        var isBlocked = reviewResult.blocked;
        var showCrisis = reviewResult.show_crisis;
        return (
          <div className="mt-3 card-surface p-4 space-y-2" data-testid="inline-review-panel">
            {/* Grade + Explanation */}
            <div className={'p-3 rounded-xl border ' + grade.border + ' bg-[#F4F2FB]'}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={'px-2.5 py-0.5 rounded-full text-xs font-bold text-white ' + grade.bg}>{reviewResult.category}</span>
                <span className={'text-xs font-semibold ' + grade.text}>{grade.label}</span>
              </div>
              <p className="text-xs text-[color:var(--text-muted)] leading-relaxed">{reviewResult.explanation || grade.desc}</p>
            </div>

            {/* Your Original */}
            <div className="p-2.5 bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-lg">
              <p className="text-xs text-[color:var(--text-soft)] uppercase tracking-wider mb-1">Your Message</p>
              <p className="text-xs text-[color:var(--text-muted)]">{pendingText}</p>
            </div>

            {/* AI Rewrite */}
            {!isBlocked && reviewResult.rewrite && (
              <div className="p-2.5 bg-[#10B981]/[0.05] border border-[#10B981]/20 rounded-lg">
                <p className="text-xs text-[#10B981] uppercase tracking-wider mb-1 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> Calmer Alternative</p>
                <p className="text-xs text-[color:var(--text-secondary)] leading-relaxed">{reviewResult.rewrite}</p>
              </div>
            )}

            {/* Crisis Support */}
            {showCrisis && (
              <div className="p-2.5 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg" data-testid="crisis-notice">
                <div className="flex items-start gap-2">
                  <Phone className="w-4 h-4 text-[#EF4444] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-[#EF4444]">Crisis Support</p>
                    <p className="text-xs text-[color:var(--text-muted)] mt-0.5">Call or text <strong className="text-[#DC2626]">988</strong> for immediate crisis support. If in danger, call <strong className="text-[#DC2626]">911</strong>.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            {isBlocked ? (
              <div className="space-y-2">
                <div className="p-2.5 bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg text-center">
                  <p className="text-xs text-[#EF4444] font-medium">This message cannot be sent. It has been logged.</p>
                </div>
                <button onClick={function () { setReviewResult(null); setReviewing(false); }}
                  className="w-full py-2 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] rounded-lg text-xs hover:bg-[#F8F7FC]"
                  data-testid="review-dismiss-btn">Dismiss</button>
              </div>
            ) : (
              <div className="flex gap-2">
                {reviewResult.rewrite && (
                  <button onClick={function () { actualSend(reviewResult.rewrite, 'rewrite'); }}
                    className="flex-1 py-2 bg-gradient-to-r from-[#10B981] to-[#5A4E9E] text-white rounded-lg text-xs font-medium"
                    data-testid="review-send-rewrite-btn">
                    Send Calmer Version
                  </button>
                )}
                <button onClick={function () { actualSend(pendingText, 'original'); }}
                  className={'py-2 px-3 rounded-lg text-xs font-medium border ' +
                    (reviewResult.category === 'Orange' ? 'bg-[#F97316]/10 border-[#F97316]/30 text-[#F97316]' : 'bg-[#F4F2FB] border-[color:var(--border-subtle)] text-[color:var(--text-muted)]') +
                    (reviewResult.rewrite ? '' : ' flex-1')}
                  data-testid="review-send-original-btn">
                  Send Original
                </button>
                <button onClick={function () { setInput(pendingText); setReviewResult(null); setReviewing(false); }}
                  className="py-2 px-3 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-soft)] rounded-lg text-xs hover:bg-[#F8F7FC]"
                  data-testid="review-edit-btn">
                  <Edit3 className="w-3 h-3 inline mr-1" />Edit
                </button>
                <button onClick={function () { setReviewResult(null); setReviewing(false); }}
                  className="py-2 px-3 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-soft)] rounded-lg text-xs hover:bg-[#F8F7FC]"
                  data-testid="review-cancel-btn">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ==================== COMMS CHECK (standalone analysis) ==================== */
function CommsCheck() {
  var [messageText, setMessageText] = useState('');
  var [messageFrom, setMessageFrom] = useState('user');
  var [analysis, setAnalysis] = useState(null);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');

  var getScoreDetails = function (score) {
    return GRADE_STYLES[score] || GRADE_STYLES.Blue;
  };

  var handleAnalyze = async function () {
    if (!messageText.trim()) { setError('Please enter a message to analyze'); return; }
    setError(''); setLoading(true);
    try {
      var res = await axios.post(API + '/comms/analyze', { message_text: messageText, message_from: messageFrom }, { withCredentials: true });
      setAnalysis(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Analysis failed');
    } finally { setLoading(false); }
  };

  var copyToClipboard = function (text) { navigator.clipboard.writeText(text); };
  var exportToPDF = async function () {
    if (!analysis) return;
    await ensureImagesLoaded();
    var pdf = new jsPDF();
    pdf.setFont('times', 'normal');
    var pageWidth = pdf.internal.pageSize.getWidth();
    var y = addPdfHeader(pdf);
    pdf.setFont('times', 'bold'); pdf.setFontSize(14); pdf.setTextColor(0, 0, 0); pdf.text('Communication Analysis', 15, y); y += 8;
    pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
    pdf.text('Score: ' + analysis.score, 15, y); pdf.text('Date: ' + new Date().toLocaleString(), 100, y); y += 10;
    pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(0, 0, 0); pdf.text('Original Message:', 15, y); y += 7;
    pdf.setFont('times', 'normal'); pdf.setFontSize(10);
    var lines = pdf.splitTextToSize(messageText, pageWidth - 30); pdf.text(lines, 15, y); y += lines.length * 7 + 8;
    if (analysis.rewritten_message && messageFrom === 'user') {
      pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.text('Suggested Rewrite:', 15, y); y += 7;
      pdf.setFont('times', 'normal'); pdf.setFontSize(10);
      var rwLines = pdf.splitTextToSize(analysis.rewritten_message, pageWidth - 30); pdf.text(rwLines, 15, y);
    }
    addPdfFooterToAllPages(pdf);
    pdf.save('parentum-communication-' + analysis.score + '-' + Date.now() + '.pdf');
  };

  var scoreInfo = analysis ? getScoreDetails(analysis.score) : null;

  return (
    <div className="space-y-6">
      <div className="card-surface p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Message From</label>
          <div className="flex gap-4">
            <button onClick={function () { setMessageFrom('user'); }}
              className={'px-4 py-2 rounded-lg transition-colors ' + (messageFrom === 'user' ? 'bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white' : 'bg-[#F4F2FB] text-[color:var(--text-muted)] hover:bg-[#F8F7FC]')}>
              My Draft Message
            </button>
            <button onClick={function () { setMessageFrom('coparent'); }}
              className={'px-4 py-2 rounded-lg transition-colors ' + (messageFrom === 'coparent' ? 'bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white' : 'bg-[#F4F2FB] text-[color:var(--text-muted)] hover:bg-[#F8F7FC]')}>
              Co-Parent Message
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Message Text</label>
          <textarea value={messageText} onChange={function (e) { setMessageText(e.target.value); }}
            className="w-full h-48 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-white placeholder:text-[color:var(--text-soft)] focus:ring-2 focus:ring-[#5A4E9E] focus:border-transparent rounded-lg p-4 resize-none"
            placeholder="Paste or type the message here..." />
        </div>
        {error && <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] rounded-lg p-3 text-sm">{error}</div>}
        <button onClick={handleAnalyze} disabled={loading || !messageText.trim()}
          className="w-full bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 font-semibold rounded-lg py-3 disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? (<><div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-r-transparent"></div> Analyzing...</>) : (<><Sparkles className="w-5 h-5" /> Analyze Message</>)}
        </button>
      </div>

      {analysis && (
        <div className="space-y-6">
          <div className="card-surface p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Analysis Result</h3>
              <button onClick={exportToPDF} className="px-4 py-2 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-white hover:bg-[#F8F7FC] rounded-lg flex items-center gap-2 text-sm" data-testid="comm-export-pdf-btn">
                <Download className="w-4 h-4" /> Export PDF
              </button>
            </div>
            <div className="flex items-start gap-4">
              <div className={'px-6 py-3 rounded-full font-bold text-lg text-white shadow-lg ' + scoreInfo.bg}>{analysis.score}</div>
              <div className="flex-1">
                <p className="font-semibold text-lg mb-1">{scoreInfo.label}</p>
                <p className="text-[color:var(--text-muted)] text-sm mb-2">{scoreInfo.desc}</p>
                <p className="text-[color:var(--text-secondary)]">{analysis.explanation}</p>
              </div>
            </div>
          </div>
          {messageFrom === 'user' && analysis.rewritten_message && (
            <div className="card-surface p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#10B981]" /> Suggested Rewrite</h3>
                <button onClick={function () { copyToClipboard(analysis.rewritten_message); }} className="text-sm text-[color:var(--text-muted)] hover:text-white flex items-center gap-1"><Copy className="w-4 h-4" /> Copy</button>
              </div>
              <div className="bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-lg p-4">
                <p className="text-white leading-relaxed">{analysis.rewritten_message}</p>
              </div>
            </div>
          )}
          {analysis.suggestions && analysis.suggestions.length > 0 && (
            <div className="card-surface p-6">
              <h3 className="text-lg font-bold mb-3">Improvement Suggestions</h3>
              <ul className="space-y-2">
                {analysis.suggestions.map(function (s, i) {
                  return <li key={i} className="flex items-start gap-2 text-[color:var(--text-secondary)]"><span className="text-[#10B981] mt-1">-</span><span>{s}</span></li>;
                })}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-lg p-4">
        <p className="text-xs text-[color:var(--text-muted)] text-center">Educational only. Not legal advice. Does not diagnose mental health conditions.</p>
      </div>
    </div>
  );
}

/* ==================== INLINE COMMS CHECK (merged into Messages tab) ==================== */
function InlineCommsCheck() {
  var [open, setOpen] = useState(false);
  var [messageText, setMessageText] = useState('');
  var [messageFrom, setMessageFrom] = useState('user');
  var [analysis, setAnalysis] = useState(null);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');

  var handleAnalyze = async function () {
    if (!messageText.trim()) { setError('Please enter a message'); return; }
    setError(''); setLoading(true);
    try {
      var res = await axios.post(API + '/comms/analyze', { message_text: messageText, message_from: messageFrom }, { withCredentials: true });
      setAnalysis(res.data);
    } catch (err) { setError(err.response?.data?.detail || 'Analysis failed'); }
    finally { setLoading(false); }
  };

  var exportToPDF = async function () {
    if (!analysis) return;
    await ensureImagesLoaded();
    var pdf = new jsPDF();
    pdf.setFont('times', 'normal');
    var pw = pdf.internal.pageSize.getWidth();
    var y = addPdfHeader(pdf);
    pdf.setFont('times', 'bold'); pdf.setFontSize(14); pdf.setTextColor(0, 0, 0); pdf.text('Communication Analysis', 15, y); y += 8;
    pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
    pdf.text('Score: ' + analysis.score, 15, y); pdf.text('Date: ' + new Date().toLocaleString(), 100, y); y += 10;
    pdf.setFontSize(10); pdf.setTextColor(0, 0, 0); pdf.text('Original Message:', 15, y); y += 6;
    pdf.setFontSize(9);
    var lines = pdf.splitTextToSize(messageText, pw - 30); pdf.text(lines, 15, y); y += lines.length * 5 + 8;
    if (analysis.rewritten_message && messageFrom === 'user') {
      pdf.setFontSize(10); pdf.text('Suggested Rewrite:', 15, y); y += 6; pdf.setFontSize(9);
      var rwLines = pdf.splitTextToSize(analysis.rewritten_message, pw - 30); pdf.text(rwLines, 15, y);
    }
    addPdfFooterToAllPages(pdf);
    pdf.save('parentum-comms-check-' + Date.now() + '.pdf');
  };

  var scoreInfo = analysis ? (GRADE_STYLES[analysis.score] || GRADE_STYLES.Blue) : null;

  return (
    <div className="card-surface overflow-hidden" data-testid="inline-comms-check">
      <button onClick={function () { setOpen(!open); }}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#F4F2FB] transition-colors"
        data-testid="comms-check-toggle">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold">Comms Check</p>
            <p className="text-xs text-[color:var(--text-soft)]">Analyze any message before sending</p>
          </div>
        </div>
        <ChevronDown className={'w-4 h-4 text-[color:var(--text-soft)] transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-[color:var(--border-subtle)]">
          <div className="flex gap-2 pt-4">
            <button onClick={function () { setMessageFrom('user'); }}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-all ' + (messageFrom === 'user' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-[#F4F2FB] text-[color:var(--text-soft)] border border-[color:var(--border-subtle)]')}
              data-testid="comms-from-user">
              My Draft
            </button>
            <button onClick={function () { setMessageFrom('coparent'); }}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-all ' + (messageFrom === 'coparent' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-[#F4F2FB] text-[color:var(--text-soft)] border border-[color:var(--border-subtle)]')}
              data-testid="comms-from-coparent">
              Co-Parent's Message
            </button>
          </div>

          <textarea value={messageText} onChange={function (e) { setMessageText(e.target.value); }}
            className="w-full h-24 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-white text-xs placeholder:text-[color:var(--text-soft)] rounded-xl p-3 resize-none focus:outline-none focus:border-[#5A4E9E]/40"
            placeholder="Paste or type the message you want to analyze..."
            data-testid="comms-check-textarea" />

          {error && <p className="text-xs text-[#EF4444]">{error}</p>}

          <button onClick={handleAnalyze} disabled={loading || !messageText.trim()}
            className="w-full py-2.5 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg text-xs font-semibold disabled:opacity-30 flex items-center justify-center gap-2"
            data-testid="comms-check-analyze-btn">
            {loading ? (<><div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"></div> Analyzing...</>) : (<><Sparkles className="w-3.5 h-3.5" /> Analyze Message</>)}
          </button>

          {/* Analysis Result */}
          {analysis && scoreInfo && (
            <div className="space-y-3 pt-2 border-t border-[color:var(--border-subtle)]">
              <div className="flex items-center gap-3">
                <div className={'px-4 py-1.5 rounded-full font-bold text-sm text-white ' + scoreInfo.bg}>{analysis.score}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{scoreInfo.label}</p>
                  <p className="text-xs text-[color:var(--text-soft)]">{scoreInfo.desc}</p>
                </div>
                <button onClick={exportToPDF} className="p-1.5 rounded-lg hover:bg-[#F4F2FB] text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)]" title="Export PDF" data-testid="comms-check-export-pdf">
                  <Download className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-[color:var(--text-muted)] leading-relaxed">{analysis.explanation}</p>

              {messageFrom === 'user' && analysis.rewritten_message && (
                <div className="p-3 bg-[#10B981]/[0.06] border border-[#10B981]/20 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-[#10B981] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Suggested Rewrite</p>
                    <button onClick={function () { navigator.clipboard.writeText(analysis.rewritten_message); }} className="text-xs text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)] flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                  </div>
                  <p className="text-xs text-[color:var(--text-secondary)] leading-relaxed">{analysis.rewritten_message}</p>
                </div>
              )}

              {analysis.suggestions && analysis.suggestions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-bold text-[color:var(--text-soft)]">Tips</p>
                  {analysis.suggestions.map(function (s, i) { return <p key={i} className="text-xs text-[color:var(--text-soft)] flex items-start gap-1.5"><span className="text-[#5A4E9E]">-</span>{s}</p>; })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ==================== MAIN COMMUNICATION PAGE ==================== */
var Communication = function ({ ws }) {
  var [hasConsent, setHasConsent] = useState(null);

  useEffect(function () {
    axios.get(API + '/coparent-messages/consent', { withCredentials: true })
      .then(function (r) { setHasConsent(r.data.consented); })
      .catch(function () { setHasConsent(true); });
  }, []);

  function handleAcceptConsent() {
    axios.post(API + '/coparent-messages/consent', {}, { withCredentials: true })
      .then(function () { setHasConsent(true); });
  }

  return (
    <div className="space-y-6">
      {/* Safety Notice */}
      <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4" data-testid="safety-notice">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-[#EF4444] mb-1">Safety Notice</p>
            <p className="text-[color:var(--text-secondary)]">If you have a protective order or are in danger, call <strong>911</strong>. National DV Hotline: 1-800-799-7233</p>
            <p className="text-[color:var(--text-muted)] mt-1">If you are experiencing suicidal thoughts, considering self-harm, or thinking about harming someone else, call or text <strong className="text-[#DC2626]">988</strong> for immediate crisis support.</p>
          </div>
        </div>
      </div>

      {hasConsent === false ? (
        <ConsentGate onAccept={handleAcceptConsent} />
      ) : (
        <>
          <CoParentChat ws={ws} />
          {/* Messaging Disclaimer Panel */}
          <div className="bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-xl p-4 space-y-2" data-testid="messaging-disclaimer">
            <p className="text-xs text-[color:var(--text-soft)] leading-relaxed font-medium">Messaging Safety Notice</p>
            <p className="text-xs text-[color:var(--text-soft)] leading-relaxed">Messages in this feature are encrypted, but you should not share financial information, medical information, information protected or restricted by court order, sealed or confidential information, passwords, account credentials, or other highly sensitive personal information in chat.</p>
            <p className="text-xs text-[color:var(--text-soft)] leading-relaxed">This messaging system may use AI to review tone before a message is sent and may offer a calmer rewrite. You remain responsible for any message you choose to send.</p>
            <p className="text-xs text-[color:var(--text-soft)] leading-relaxed">Threatening, abusive, violent, degrading, self-harm related, or harm-to-others content is not allowed and may be blocked.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default Communication;
